import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LaunchModal from '../components/LaunchModal';

const PricingPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="bg-gray-950 text-white antialiased">
      {/* Hero Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Choose the plan that fits your horse operation. No hidden fees, no complicated contracts. 
            Start your modern farm management journey today.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>30-day money-back guarantee</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>No setup fees</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-semibold text-white mb-2">Starter</h3>
                <p className="text-gray-400 mb-4">Perfect for smaller operations</p>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-2xl text-gray-500 line-through">$40</span>
                  <span className="text-5xl font-bold text-white">$35</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-blue-400 mt-2">Launch pricing - save $60/year!</p>
              </div>
              
              <ul className="space-y-4 mb-8 text-gray-300">
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Up to 15 horses</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Up to 10 clients</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Up to 5 team members</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>All core features</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Mobile apps (iOS, Android)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Payment processing (Stripe)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Email support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-500">Free data migration</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-500">Custom integrations</span>
                </li>
              </ul>
              
              <button 
                onClick={() => setIsModalOpen(true)}
                className="w-full text-center border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
              >
                Get Notified at Launch
              </button>
            </div>
            
            {/* Unlimited Plan */}
            <div className="bg-gray-800 border-2 border-blue-500 rounded-xl p-8 relative transform scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="brand-gradient text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-semibold text-white mb-2">Unlimited</h3>
                <p className="text-gray-400 mb-4">For growing and large operations</p>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-2xl text-gray-500 line-through">$85</span>
                  <span className="text-5xl font-bold text-white">$75</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-blue-400 mt-2">First year pricing - save $120!</p>
              </div>
              
              <ul className="space-y-4 mb-8 text-gray-300">
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited horses</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited clients</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited team members</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>All features + plugins</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Advanced reporting & analytics</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Priority phone & email support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Free data migration</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-400">Basic custom integrations</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-500">Dedicated account manager</span>
                </li>
              </ul>
              
              <button 
                onClick={() => setIsModalOpen(true)}
                className="w-full text-center brand-gradient text-white px-6 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                Get Notified at Launch
              </button>
            </div>
            
            {/* Ranch Scale Plan */}
            <div className="bg-gray-800 border border-purple-500 rounded-xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Enterprise Scale
                </span>
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-semibold text-white mb-2">Enterprise Scale</h3>
                <p className="text-gray-400 mb-4">For major operations (1000+ horses)</p>
                <div className="flex items-center justify-center">
                  <span className="text-4xl font-bold text-white">Custom</span>
                </div>
                <p className="text-sm text-purple-400 mt-2">Tailored to your operation</p>
              </div>
              
              <ul className="space-y-4 mb-8 text-gray-300">
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited everything</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Multi-location support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Advanced reporting & analytics</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Dedicated account manager</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>White-label options</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Full custom integrations</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>24/7 priority support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>On-site training & setup</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Custom compliance reporting</span>
                </li>
              </ul>
              
              <Link 
                to="/contact" 
                className="w-full text-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25 block"
              >
                Contact Sales
              </Link>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <p className="text-gray-400 mb-4 text-lg">
              All plans include: Mobile apps, credit card processing, automatic invoicing, 
              data exports, and 99.9% uptime guarantee
            </p>
            <p className="text-sm text-gray-500">
              No setup fees • Cancel anytime • 30-day money-back guarantee
            </p>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Compare All Features</h2>
            <p className="text-gray-400">Everything you need to know about our plans</p>
          </div>
          
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="text-left p-6 text-white font-semibold">Features</th>
                    <th className="text-center p-6 text-white font-semibold">Starter</th>
                    <th className="text-center p-6 text-white font-semibold">Unlimited</th>
                    <th className="text-center p-6 text-white font-semibold">Enterprise Scale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  <tr>
                    <td className="p-6 text-gray-300 font-medium">Horse Records</td>
                    <td className="p-6 text-center text-gray-400">Up to 15</td>
                    <td className="p-6 text-center text-green-400">Unlimited</td>
                    <td className="p-6 text-center text-green-400">Unlimited</td>
                  </tr>
                  <tr className="bg-gray-850">
                    <td className="p-6 text-gray-300 font-medium">Client Management</td>
                    <td className="p-6 text-center text-gray-400">Up to 10</td>
                    <td className="p-6 text-center text-green-400">Unlimited</td>
                    <td className="p-6 text-center text-green-400">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="p-6 text-gray-300 font-medium">Team Members</td>
                    <td className="p-6 text-center text-gray-400">Up to 5</td>
                    <td className="p-6 text-center text-green-400">Unlimited</td>
                    <td className="p-6 text-center text-green-400">Unlimited</td>
                  </tr>
                  <tr className="bg-gray-850">
                    <td className="p-6 text-gray-300 font-medium">Automated Billing</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr>
                    <td className="p-6 text-gray-300 font-medium">Payment Processing</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr className="bg-gray-850">
                    <td className="p-6 text-gray-300 font-medium">Mobile Apps</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr>
                    <td className="p-6 text-gray-300 font-medium">Advanced Reporting</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr className="bg-gray-850">
                    <td className="p-6 text-gray-300 font-medium">Free Data Migration</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr>
                    <td className="p-6 text-gray-300 font-medium">Custom Integrations</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-yellow-400">Basic</td>
                    <td className="p-6 text-center text-green-400">Full</td>
                  </tr>
                  <tr className="bg-gray-850">
                    <td className="p-6 text-gray-300 font-medium">Multi-Location Support</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr>
                    <td className="p-6 text-gray-300 font-medium">Dedicated Account Manager</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-red-400">✗</td>
                    <td className="p-6 text-center text-green-400">✓</td>
                  </tr>
                  <tr className="bg-gray-850">
                    <td className="p-6 text-gray-300 font-medium">Support</td>
                    <td className="p-6 text-center text-yellow-400">Email</td>
                    <td className="p-6 text-center text-green-400">Priority Phone & Email</td>
                    <td className="p-6 text-center text-green-400">24/7 Priority</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Pricing Questions?</h2>
            <p className="text-gray-400">Common questions about our pricing and plans</p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Can I change plans later?</h3>
              <p className="text-gray-400">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, 
                and we'll prorate the billing accordingly.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">What payment methods do you accept?</h3>
              <p className="text-gray-400 mb-6">
                We accept all major credit cards (Visa, Mastercard, American Express) and ACH bank transfers.
                <strong> Mailed checks are completely free</strong> with automated tracking and deposit reminders.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Is there a setup fee?</h3>
              <p className="text-gray-400">
                No setup fees for Starter and Unlimited plans. Ranch Scale customers receive complimentary setup 
                and onboarding as part of their custom package.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Do you offer discounts for annual billing?</h3>
              <p className="text-gray-400">
                Yes! Pay annually and save 2 months (16.7% discount). Our launch pricing already includes 
                significant savings - annual billing provides even more value.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">What happens to my data if I cancel?</h3>
              <p className="text-gray-400">
                Your data remains accessible for 90 days after cancellation. We provide a complete export 
                of all your information in standard formats before your account is closed.
              </p>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Do you charge transaction fees?</h3>
              <p className="text-gray-400">
                BarnBills doesn't add any fees on top of standard payment processing. You'll pay standard Stripe rates 
                (2.9% + 30¢ for cards, 0.8% for ACH capped at $5) with no markup from us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Modernize Your Horse Farm?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Join hundreds of farms ready to upgrade from 1985-era software to modern, 
            professional farm management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
            >
              Get Notified at Launch
            </button>
            <Link 
              to="/contact?subject=demo&message=I%20am%20interested%20in%20demoing%20the%20BarnBills%20Dashboard.%20I%20am%20interested%20mainly%20in...%20and%20I%20have%20available%20from...%20to...&scrollTo=message" 
              className="border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
            >
              Schedule a Demo
            </Link>
          </div>
          <p className="text-gray-500 text-sm mt-6">
            30-day money-back guarantee • No setup fees • Cancel anytime
          </p>
        </div>
      </section>

      <LaunchModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default PricingPage; 